// Equity + Drawdown panel (2026-07-15) — merges the former side-by-side
// Equity Curve and Drawdown panels into ONE full-width panel with a runtime
// view toggle (no reload):
//   · stacked — tall equity pane + slim drawdown strip, shared time axis,
//     synced crosshair (TradingView-style). Default.
//   · overlay — single plot, ONE money axis (no right-side scale); drawdown
//     as a red range band hanging off the BALANCE line: [balance − |dd|,
//     balance]. The band's bottom edge is the level the account fell to,
//     so drawdown size reads directly against balance/equity.
//
// Drawdown is graphed in ACCOUNT CURRENCY (2026-07-15) so it reads in the
// same units as the equity pane and is directly comparable at a glance;
// the % is secondary (tooltip + trough-marker label). The stacked strip
// shows dd as depth-from-zero; the overlay shows it as a level band — two
// genuinely different views of the same series.
//
// Presentational only — the parent (account-detail) owns the data fetches and
// passes the raw series in. Both modes are driven by ONE merged time-indexed
// array so the recharts syncId crosshair (which matches by index) lines up
// exactly across the two stacked panes.
//
// DD basis contract (unchanged from the split panels): prefer the server
// series (deposit-adjusted, equity-inclusive — matches the Max DD card); fall
// back to the client hourly balance-only estimate WITH the amber
// "understates dd" badge. Never show estimated data unlabeled.

import { useMemo, useState } from 'react';
import {
  Area, CartesianGrid, ComposedChart, Line, ReferenceArea, ReferenceDot,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { fmtMoney } from '@/lib/utils';
import { Chip, Panel, PanelBody, PanelHead } from '@/components/ui';
import type { EquityPoint } from '@/lib/metrics';

// Shared chart styling — same values as account-detail.tsx / the other chart
// panels. Deliberate small duplication (2026-05-28 refactor convention) to
// keep this component self-contained.
const AXIS = { stroke: '#4b5563', fontSize: 10, fontFamily: 'JetBrains Mono' };
const GRID = '#1a2029';
const TooltipStyle = {
  backgroundColor: '#0f1115',
  border: '1px solid #232a35',
  borderRadius: 6,
  fontSize: 11,
  color: '#f3f4f6',
};

// Structural subset of the parent's BalanceOpRow — only what the deposit /
// withdrawal reference markers need.
export type CapitalFlowMarker = {
  id: number;
  snapshot_ts: string;   // ISO
  amount: number;        // signed: deposits > 0, withdrawals < 0
};

type DdServerPoint = { bucket: string; ddPct: number; ddAbs: number };

// Equity Protector config (veritas.account_user_metadata, per user+account).
// BOTH modes anchor to CURRENT BALANCE and step whenever balance steps
// (anchor decisions by Gooseman, 2026-07-15):
//   'pct': threshold = balance × (1 − value/100)
//   'abs': threshold = balance − value  (value = tolerated loss in account
//          currency below current balance — NOT a fixed equity floor)
// Rendered on the money axis only — deliberately NOT in the DD strip: the
// strip measures distance from PEAK, a different anchor, and drawing a
// balance-anchored threshold there would misstate when the protector fires.
export type EquityProtector = { mode: 'pct' | 'abs'; value: number };
type MergedPoint = {
  ts: number;
  balance?: number;
  equity?: number | null;
  ddPct?: number;
  ddAbs?: number;   // drawdown in account currency — the number an equity protector needs
  epThr?: number;   // equity-protector threshold: balance × (1 − v/100) or balance − v
  ddBand?: [number, number]; // overlay range area: [balance + ddAbs, balance] — dd as a level band off the balance line
};
type EqDdMode = 'stacked' | 'overlay';

// View-mode preference survives reloads + account switches (global, not
// per-account — layout taste, not data). Same localStorage pattern as
// thesis.tsx COLLAPSE_KEY.
const MODE_LS_KEY = 'veritas:eqdd-view-mode';

export function EquityDrawdownPanel({
  equity,
  ddClient,
  ddServer,
  capitalFlowMarkers,
  equityProtector = null,
  onSaveEquityProtector,
}: {
  equity: EquityPoint[];
  ddClient: { t: string; ddPct: number; ddAbs: number }[];
  ddServer: DdServerPoint[] | null;
  capitalFlowMarkers: CapitalFlowMarker[];
  equityProtector?: EquityProtector | null;
  // Absent for read-only viewers — the editor affordance is hidden.
  onSaveEquityProtector?: (ep: EquityProtector | null) => Promise<void>;
}) {
  const [mode, setModeState] = useState<EqDdMode>(() => {
    try {
      return localStorage.getItem(MODE_LS_KEY) === 'overlay' ? 'overlay' : 'stacked';
    } catch {
      return 'stacked';
    }
  });
  const setMode = (m: EqDdMode) => {
    setModeState(m);
    try { localStorage.setItem(MODE_LS_KEY, m); } catch { /* private mode — session-only */ }
  };

  // ── drag-to-zoom (CoinGecko-style) ──────────────────────────────────
  // Drag a region on any pane to zoom the shared time axis (min 60s so a
  // plain click never zooms); double-click or the Reset chip restores the
  // full range. While zoomed, the y axes rescale to the visible window —
  // this is also what makes exact peaks/troughs hoverable: at full range
  // the series packs several data points per pixel and recharts can only
  // snap the crosshair to one of them.
  const [zoom, setZoom] = useState<[number, number] | null>(null);
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [dragEnd, setDragEnd] = useState<number | null>(null);

  // Structural subset of recharts' CategoricalChartState (activeLabel is
  // the x value under the cursor — epoch ms on our numeric axis).
  type ChartMouseState = { activeLabel?: string | number } | null;
  const onDragDown = (e: ChartMouseState) => {
    if (e && e.activeLabel != null) {
      setDragStart(Number(e.activeLabel));
      setDragEnd(null);
    }
  };
  const onDragMove = (e: ChartMouseState) => {
    if (dragStart != null && e && e.activeLabel != null) setDragEnd(Number(e.activeLabel));
  };
  const onDragUp = () => {
    if (dragStart != null && dragEnd != null) {
      const a = Math.min(dragStart, dragEnd);
      const b = Math.max(dragStart, dragEnd);
      if (b - a >= 60_000) setZoom([a, b]);
    }
    setDragStart(null);
    setDragEnd(null);
  };
  const cancelDrag = () => { setDragStart(null); setDragEnd(null); };
  const resetZoom = () => setZoom(null);

  // ── equity-protector editor state ───────────────────────────────────
  const [epEditing, setEpEditing] = useState(false);
  const [epMode, setEpMode] = useState<'pct' | 'abs'>(equityProtector?.mode ?? 'pct');
  const [epInput, setEpInput] = useState(equityProtector ? String(equityProtector.value) : '');
  const [epSaving, setEpSaving] = useState(false);

  const openEpEditor = () => {
    setEpMode(equityProtector?.mode ?? 'pct');
    setEpInput(equityProtector ? String(equityProtector.value) : '');
    setEpEditing(true);
  };
  const saveEp = async (next: EquityProtector | null) => {
    if (!onSaveEquityProtector) return;
    setEpSaving(true);
    try {
      await onSaveEquityProtector(next);
      setEpEditing(false);
    } catch {
      /* error surfaced by the parent (page error banner) — keep editor open */
    } finally {
      setEpSaving(false);
    }
  };
  const epInputValue = Number(epInput);
  const epInputValid =
    Number.isFinite(epInputValue) && epInputValue > 0 && (epMode !== 'pct' || epInputValue < 100);

  const serverBasis = !!(ddServer && ddServer.length);

  // ── series prep ─────────────────────────────────────────────────────
  // Time-based (numeric) X axis — same rationale as the original panels: a
  // day with 1000 snapshots must not get more pixel width than a day with 30.
  const equityTs = useMemo(
    () => equity.map((p) => ({ ts: Date.parse(p.t), balance: p.balance, equity: p.equity })),
    [equity],
  );
  const ddTs = useMemo(() => {
    // Prefer the server series (deposit-adjusted, equity-inclusive,
    // raw-sourced — matches the Max DD card). It carries positive
    // magnitudes, so negate to dip below zero like the client convention.
    if (ddServer && ddServer.length) {
      return ddServer.map((p) => ({ ts: Date.parse(p.bucket), ddPct: -p.ddPct, ddAbs: -p.ddAbs }));
    }
    return ddClient.map((p) => ({ ts: Date.parse(p.t), ddPct: p.ddPct, ddAbs: p.ddAbs }));
  }, [ddServer, ddClient]);

  // ONE merged array drives both modes. Equity is hourly+raw, the server DD
  // series is daily — buckets that only exist on one side keep the other
  // side's fields undefined and the lines bridge them via connectNulls
  // (visually identical to the old two-array rendering). No forward-fill:
  // we don't fabricate intra-day drawdown the server never computed.
  const merged = useMemo<MergedPoint[]>(() => {
    const map = new Map<number, MergedPoint>();
    for (const p of equityTs) {
      if (Number.isFinite(p.ts)) map.set(p.ts, { ts: p.ts, balance: p.balance, equity: p.equity });
    }
    for (const p of ddTs) {
      if (!Number.isFinite(p.ts)) continue;
      const e = map.get(p.ts);
      if (e) { e.ddPct = p.ddPct; e.ddAbs = p.ddAbs; }
      else map.set(p.ts, { ts: p.ts, ddPct: p.ddPct, ddAbs: p.ddAbs });
    }
    return Array.from(map.values()).sort((a, b) => a.ts - b.ts);
  }, [equityTs, ddTs]);

  // Derived per-point series on top of the merged array:
  //
  // · epThr — equity-protector threshold. BOTH modes anchor to CURRENT
  //   BALANCE (correction 2026-07-15 — 'abs' is NOT a fixed equity floor):
  //   'pct' → balance × (1 − value/100); 'abs' → balance − value.
  //
  // · ddBand — the overlay's drawdown band [balance + ddAbs, balance]:
  //   drawdown drawn as the LEVEL the account fell to, hanging off the
  //   balance line, so its size reads directly against balance/equity.
  //   Balance is forward-filled onto dd-only (daily) buckets — exact, not
  //   interpolation: balance only changes at recorded points (steps on
  //   close), so "last seen balance" IS the balance at that moment.
  const chartData = useMemo<MergedPoint[]>(() => {
    const thr = equityProtector
      ? (b: number) =>
          equityProtector.mode === 'pct'
            ? b * (1 - equityProtector.value / 100)
            : b - equityProtector.value
      : null;
    let lastBalance: number | null = null;
    return merged.map((p) => {
      if (p.balance != null) lastBalance = p.balance;
      const epThr = thr && p.balance != null ? thr(p.balance) : undefined;
      const ddBand: [number, number] | undefined =
        p.ddAbs != null && lastBalance != null
          ? [lastBalance + p.ddAbs, lastBalance]
          : undefined;
      if (epThr === undefined && ddBand === undefined) return p;
      return { ...p, ...(epThr !== undefined ? { epThr } : {}), ...(ddBand ? { ddBand } : {}) };
    });
  }, [merged, equityProtector]);

  // ── axis helpers ─────────────────────────────────────────────────────
  const fullDomain = useMemo<[number, number] | undefined>(() => {
    if (merged.length === 0) return undefined;
    return [merged[0].ts, merged[merged.length - 1].ts];
  }, [merged]);

  // Active x domain: the zoom window if set, else the full data range.
  const xDomain = zoom ?? fullDomain;

  // Points inside the active window — drives y-axis rescale + the
  // peak/trough markers (extremes of what you're LOOKING at, not of all
  // history).
  const visible = useMemo<MergedPoint[]>(() => {
    if (!zoom) return chartData;
    return chartData.filter((p) => p.ts >= zoom[0] && p.ts <= zoom[1]);
  }, [chartData, zoom]);

  const xTicks = useMemo<number[]>(() => {
    if (!xDomain) return [];
    const [start, end] = xDomain;
    const dayMs = 86_400_000;
    const span = end - start;
    if (span < dayMs) {
      // Sub-day window: 5 evenly spaced ticks across the range
      return [0, 0.25, 0.5, 0.75, 1].map((f) => start + f * span);
    }
    if (span <= 4 * dayMs) {
      // Zoomed to a few days: 6-hour boundaries
      const step = 6 * 3_600_000;
      const first = Math.ceil(start / step) * step;
      const out: number[] = [];
      for (let t = first; t <= end; t += step) out.push(t);
      return out;
    }
    // Multi-day: one tick at each midnight-UTC boundary inside the range
    const firstMidnight = Math.ceil(start / dayMs) * dayMs;
    const out: number[] = [];
    for (let t = firstMidnight; t <= end; t += dayMs) out.push(t);
    return out;
  }, [xDomain]);

  const isSingleDay = !!xDomain && xDomain[1] - xDomain[0] < 86_400_000;

  const fmtXTick = (v: number) => {
    const d = new Date(v);
    const hm = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
    if (isSingleDay) return hm;
    if (v % 86_400_000 !== 0) return hm; // intra-day tick on a zoomed view
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${m}-${day}`;
  };

  const fmtTooltipLabel = (v: number) => {
    const d = new Date(v);
    return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
  };

  // Money axis: 'auto' at full range (recharts computes from all plotted
  // series, epThr included); when zoomed, rescale to the visible window so
  // the curve fills the pane and every point becomes hover-distinguishable.
  // epThr is folded in so the protector line never rescales out of view.
  const moneyDomain = useMemo<[number | 'auto', number | 'auto']>(() => {
    if (!zoom) return ['auto', 'auto'];
    let lo = Infinity;
    let hi = -Infinity;
    for (const p of visible) {
      if (p.balance != null) { lo = Math.min(lo, p.balance); hi = Math.max(hi, p.balance); }
      if (p.equity  != null) { lo = Math.min(lo, p.equity);  hi = Math.max(hi, p.equity);  }
      if (p.epThr   != null) { lo = Math.min(lo, p.epThr);   hi = Math.max(hi, p.epThr);   }
      if (p.ddBand) { lo = Math.min(lo, p.ddBand[0]); hi = Math.max(hi, p.ddBand[1]); }
    }
    if (!Number.isFinite(lo)) return ['auto', 'auto'];
    const pad = Math.max((hi - lo) * 0.06, 1);
    return [lo - pad, hi + pad];
  }, [zoom, visible]);

  // Worst visible drawdown in account currency (≤ 0). DD is graphed in
  // CURRENCY on the money scale (2026-07-15 request) so it's directly
  // comparable with the balance/equity lines — % stays available in the
  // tooltip and the trough marker.
  const ddLoVisible = useMemo(
    () => visible.reduce((m, p) => (p.ddAbs != null ? Math.min(m, p.ddAbs) : m), 0),
    [visible],
  );

  // DD strip axis (currency): 0 at top; rescale to the window when zoomed.
  const stripDdDomain = useMemo<[number | 'auto', number]>(() => {
    if (!zoom) return ['auto', 0];
    return [Math.min(-1, ddLoVisible * 1.15), 0];
  }, [zoom, ddLoVisible]);


  // ── peak / trough markers ────────────────────────────────────────────
  // Labeled dots at the extremes of the visible window. This answers "what
  // was the exact top/bottom?" even when the point itself is packed too
  // densely to hover. Equity extremes prefer the equity series (falls back
  // to balance where equity is null); the DD trough comes from the dd
  // series (server basis when available — matches the Max DD card).
  const extremes = useMemo(() => {
    let peak: { ts: number; v: number } | null = null;
    let trough: { ts: number; v: number } | null = null;
    // dd trough: worst CURRENCY excursion (v = ddAbs = the strip's plotted
    // value; level = the band's low, for the overlay). The % rides along
    // for the label. Worst-by-currency can differ from worst-by-% when the
    // balance changed a lot across the window.
    let ddTrough: { ts: number; v: number; pct?: number; level?: number } | null = null;
    for (const p of visible) {
      const eq = p.equity ?? p.balance;
      if (eq != null) {
        if (!peak || eq > peak.v) peak = { ts: p.ts, v: eq };
        if (!trough || eq < trough.v) trough = { ts: p.ts, v: eq };
      }
      if (p.ddAbs != null && (!ddTrough || p.ddAbs < ddTrough.v)) {
        ddTrough = { ts: p.ts, v: p.ddAbs, pct: p.ddPct, level: p.ddBand?.[0] };
      }
    }
    return { peak, trough, ddTrough };
  }, [visible]);

  const extremeLabelStyle = { fontSize: 10, fontFamily: 'JetBrains Mono' };
  // Arrays, not fragments — recharts only walks direct children.
  const equityExtremeDots = [
    extremes.peak && (
      <ReferenceDot
        key="eq-peak"
        x={extremes.peak.ts}
        y={extremes.peak.v}
        r={3}
        fill="#00ff88"
        stroke="#0f1115"
        strokeWidth={1}
        label={{ value: fmtMoney(extremes.peak.v, ''), position: 'top', fill: '#00ff88', ...extremeLabelStyle }}
      />
    ),
    extremes.trough && (
      <ReferenceDot
        key="eq-trough"
        x={extremes.trough.ts}
        y={extremes.trough.v}
        r={3}
        fill="#ef4444"
        stroke="#0f1115"
        strokeWidth={1}
        label={{ value: fmtMoney(extremes.trough.v, ''), position: 'bottom', fill: '#ef4444', ...extremeLabelStyle }}
      />
    ),
  ];
  // 'depth' (strip): dot at ddAbs below zero. 'level' (overlay): dot at the
  // band's low — the equity level the drawdown reached off the balance line.
  const ddTroughDot = (kind: 'depth' | 'level') => {
    if (!extremes.ddTrough) return null;
    const y = kind === 'level' ? (extremes.ddTrough.level ?? extremes.ddTrough.v) : extremes.ddTrough.v;
    return (
      <ReferenceDot
        key="dd-trough"
        x={extremes.ddTrough.ts}
        y={y}
        r={3}
        fill="#ef4444"
        stroke="#0f1115"
        strokeWidth={1}
        label={{
          value: extremes.ddTrough.pct != null
            ? `${fmtMoney(extremes.ddTrough.v, '')} · ${extremes.ddTrough.pct.toFixed(2)}%`
            : fmtMoney(extremes.ddTrough.v, ''),
          position: kind === 'level' ? 'bottom' : 'top',
          fill: '#ef4444',
          ...extremeLabelStyle,
        }}
      />
    );
  };

  // Equity-protector visual for the money-axis charts (equity pane +
  // overlay). Amber, long-dash — visually distinct from the blue capital-flow
  // markers and the green/blue series. One stepping line for BOTH modes
  // (they only differ in how the offset below balance is computed). Array,
  // not fragment (recharts only walks direct children).
  const epElements = [
    equityProtector && (
      <Line
        key="ep-thr"
        type="stepAfter"
        dataKey="epThr"
        stroke="#f59e0b"
        strokeWidth={1.5}
        strokeDasharray="8 4"
        dot={false}
        activeDot={false}
        connectNulls
        isAnimationActive={false}
        legendType="none"
      />
    ),
  ];

  // Blue selection rectangle while dragging — rendered in every pane so the
  // selection is visible across the stacked pair.
  const dragSelection = dragStart != null && dragEnd != null ? (
    <ReferenceArea
      x1={Math.min(dragStart, dragEnd)}
      x2={Math.max(dragStart, dragEnd)}
      fill="#60a5fa"
      fillOpacity={0.12}
      stroke="#60a5fa"
      strokeOpacity={0.35}
    />
  ) : null;

  // Shared tooltip formatter — the merged array means one tooltip can carry
  // Balance, Equity AND Drawdown (dd row appears at dd buckets only). The
  // Drawdown row shows % AND account currency — the currency figure is the
  // actionable one (e.g. sizing an EA equity-protector threshold).
  const tooltipFormatter = (
    v: number | string | Array<number | string>,
    name: number | string,
    item?: { payload?: { ddPct?: number; ddAbs?: number } },
  ) => {
    if (name === 'ddAbs' || name === 'ddBand') {
      // ddBand's own value is the [low, high] range pair — read depth/% from
      // the point payload instead so both series report identically.
      const abs = name === 'ddBand' ? item?.payload?.ddAbs : Number(v);
      const pct = item?.payload?.ddPct;
      const pctTxt = pct != null ? ` · ${pct.toFixed(2)}%` : '';
      return [`${fmtMoney(Number(abs ?? 0), '')}${pctTxt}`, 'Drawdown'] as [string, string];
    }
    if (name === 'epThr') return [fmtMoney(Number(v), ''), 'Protector'] as [string, string];
    return [fmtMoney(Number(v), ''), name === 'equity' ? 'Equity' : 'Balance'] as [string, string];
  };

  // Capital-flow markers — vertical reference lines at deposit and
  // withdrawal moments so a $10K balance jump doesn't read as trading edge.
  // Rebates/bonuses intentionally skipped (clutter; usually small).
  const markerLines = capitalFlowMarkers.map((op) => {
    const ts = Date.parse(op.snapshot_ts);
    if (!Number.isFinite(ts)) return null;
    const inbound = op.amount > 0;
    const stroke  = inbound ? '#60a5fa' : '#f59e0b';
    const sign    = inbound ? '+' : '−';
    // Compact label: "+10K" rather than "+10,000.00".
    const abs = Math.abs(op.amount);
    const compact =
      abs >= 1000 ? `${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`
                  : abs.toFixed(0);
    return (
      <ReferenceLine
        key={op.id}
        x={ts}
        stroke={stroke}
        strokeDasharray="3 3"
        strokeWidth={1}
        label={{
          value: `${sign}$${compact}`,
          position: 'insideTop',
          fill: stroke,
          fontSize: 10,
          fontFamily: 'JetBrains Mono',
          dy: 4,
        }}
      />
    );
  });

  const ddGradient = (
    <defs>
      <linearGradient id="eqdd-dd-fill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%"   stopColor="#ef4444" stopOpacity={0.45} />
        <stop offset="100%" stopColor="#ef4444" stopOpacity={0.05} />
      </linearGradient>
    </defs>
  );

  const basisBadge = serverBasis ? (
    <span
      className="rounded-full border border-green/25 bg-green/10 px-2.5 py-0.5 text-[9.5px] font-bold uppercase tracking-eyebrow text-green"
      title="Server-computed from raw snapshots: deposit-adjusted, equity-inclusive (open floating losses count). Matches the Max Drawdown card."
    >
      server · deposit-adj
    </span>
  ) : (
    <span
      className="rounded-full border border-amber/35 bg-amber/10 px-2.5 py-0.5 text-[9.5px] font-bold uppercase tracking-eyebrow text-amber"
      title="Client estimate from the hourly balance series — open floating losses are invisible here, so the real drawdown was deeper than shown. The authoritative server series is loading."
    >
      estimated — understates dd
    </span>
  );

  return (
    <Panel>
      <PanelHead
        title={
          <>
            Equity &amp; Drawdown{' '}
            <span className="text-text-dim">
              balance steps on close · equity tracks live floating P&amp;L · drawdown = distance from peak
            </span>
          </>
        }
        right={
          <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1.5">
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-eyebrow text-text-dim">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-[2px] w-4 bg-green" /> Balance
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block h-[1.5px] w-4"
                  style={{
                    backgroundImage:
                      'repeating-linear-gradient(90deg, #60a5fa 0 4px, transparent 4px 7px)',
                  }}
                />
                Equity
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-[7px] w-4 rounded-[1px] bg-red/50" /> Drawdown
              </span>
              {equityProtector && (
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-block h-[1.5px] w-4"
                    style={{
                      backgroundImage:
                        'repeating-linear-gradient(90deg, #f59e0b 0 8px, transparent 8px 12px)',
                    }}
                  />
                  Protector
                </span>
              )}
            </div>
            {basisBadge}
            {/* Equity-protector setting — per (user, account), veritas.
                account_user_metadata via its own partial-column RPC. */}
            {onSaveEquityProtector ? (
              epEditing ? (
                <span className="inline-flex items-center gap-1">
                  <Chip active={epMode === 'pct'} alt onClick={() => setEpMode('pct')}>%</Chip>
                  <Chip active={epMode === 'abs'} alt onClick={() => setEpMode('abs')}>$</Chip>
                  <input
                    type="number"
                    value={epInput}
                    onChange={(e) => setEpInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && epInputValid) void saveEp({ mode: epMode, value: epInputValue });
                      if (e.key === 'Escape') setEpEditing(false);
                    }}
                    placeholder={epMode === 'pct' ? 'e.g. 0.75' : 'e.g. 10000'}
                    step="any"
                    min={0}
                    className="w-24 rounded border border-line bg-bg px-2 py-1 text-[11px] font-semibold text-text outline-none focus:border-amber"
                    disabled={epSaving}
                    autoFocus
                  />
                  <button
                    onClick={() => { if (epInputValid) void saveEp({ mode: epMode, value: epInputValue }); }}
                    disabled={epSaving || !epInputValid}
                    className="rounded bg-green/20 px-2 py-1 text-[10px] font-bold uppercase tracking-eyebrow text-green hover:bg-green/30 disabled:opacity-50"
                  >
                    {epSaving ? '…' : 'Save'}
                  </button>
                  {equityProtector && (
                    <button
                      onClick={() => void saveEp(null)}
                      disabled={epSaving}
                      className="rounded px-2 py-1 text-[10px] font-bold uppercase tracking-eyebrow text-red/80 hover:text-red disabled:opacity-50"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => setEpEditing(false)}
                    disabled={epSaving}
                    className="rounded px-1.5 py-1 text-[10px] uppercase tracking-eyebrow text-text-dim hover:text-text disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </span>
              ) : equityProtector ? (
                <button
                  onClick={openEpEditor}
                  className="rounded-full border border-amber/35 bg-amber/10 px-2.5 py-0.5 text-[9.5px] font-bold uppercase tracking-eyebrow text-amber hover:bg-amber/20"
                  title={
                    equityProtector.mode === 'pct'
                      ? `Equity Protector: ${equityProtector.value}% below current balance — the line tracks balance × ${(1 - equityProtector.value / 100).toFixed(4)}. Click to edit.`
                      : `Equity Protector: ${fmtMoney(equityProtector.value, '')} below current balance — the line tracks balance − ${fmtMoney(equityProtector.value, '')}. Click to edit.`
                  }
                >
                  EP {equityProtector.mode === 'pct'
                    ? `−${equityProtector.value}%`
                    : `−${fmtMoney(equityProtector.value, '')}`}
                </button>
              ) : (
                <button
                  onClick={openEpEditor}
                  className="rounded-full border border-line px-2.5 py-0.5 text-[9.5px] font-bold uppercase tracking-eyebrow text-text-dim hover:border-amber/35 hover:text-amber"
                  title="Draw your EA's equity-protector threshold on the chart — a % or a currency amount below current balance. Stored per account."
                >
                  set protector
                </button>
              )
            ) : (
              equityProtector && (
                <span
                  className="rounded-full border border-amber/35 bg-amber/10 px-2.5 py-0.5 text-[9.5px] font-bold uppercase tracking-eyebrow text-amber"
                  title={
                    equityProtector.mode === 'pct'
                      ? `Equity Protector: ${equityProtector.value}% below current balance.`
                      : `Equity Protector: ${fmtMoney(equityProtector.value, '')} below current balance.`
                  }
                >
                  EP {equityProtector.mode === 'pct'
                    ? `−${equityProtector.value}%`
                    : `−${fmtMoney(equityProtector.value, '')}`}
                </span>
              )
            )}
            {zoom ? (
              <Chip active alt onClick={resetZoom}>Reset zoom</Chip>
            ) : (
              <span className="hidden text-[9.5px] uppercase tracking-eyebrow text-text-dim md:inline">
                drag to zoom
              </span>
            )}
            <div className="flex items-center">
              <Chip active={mode === 'stacked'} onClick={() => setMode('stacked')}>Stacked</Chip>
              <Chip active={mode === 'overlay'} onClick={() => setMode('overlay')}>Overlay</Chip>
            </div>
          </div>
        }
      />
      <PanelBody>
        {mode === 'stacked' ? (
          <>
            {/* ── equity pane ── x-axis hidden; labels live on the dd strip
                 below. syncId links the crosshairs; both charts share the
                 SAME merged array so index-based sync aligns exactly. */}
            <div
              className="h-[220px] select-none sm:h-[260px] md:h-[300px]"
              onDoubleClick={resetZoom}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData}
                  syncId="eqdd"
                  onMouseDown={onDragDown}
                  onMouseMove={onDragMove}
                  onMouseUp={onDragUp}
                  onMouseLeave={cancelDrag}
                >
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis
                    type="number"
                    dataKey="ts"
                    domain={xDomain}
                    ticks={xTicks}
                    hide
                    allowDataOverflow
                  />
                  <YAxis
                    tick={AXIS}
                    domain={moneyDomain}
                    width={70}
                    allowDataOverflow
                    tickFormatter={(v) => v.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  />
                  {/* Hidden % axis for the invisible dd tooltip-line below —
                      without it, ddPct (≈ -4) would drag the money scale to
                      zero and flatten the equity curve. */}
                  <YAxis yAxisId="dd" hide domain={['auto', 'auto']} />
                  <Tooltip
                    contentStyle={TooltipStyle}
                    labelStyle={{ color: '#9ca3af' }}
                    formatter={tooltipFormatter}
                    labelFormatter={(l) => fmtTooltipLabel(l as number)}
                  />
                  {markerLines}
                  {/* Balance: solid green, steps on close */}
                  <Line
                    type="monotone"
                    dataKey="balance"
                    stroke="#00ff88"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                  {/* Equity: blue dashed, continuous (shows floating P&L) */}
                  <Line
                    type="monotone"
                    dataKey="equity"
                    stroke="#60a5fa"
                    strokeWidth={1.5}
                    strokeDasharray="3 3"
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                  {/* Invisible dd series — exists only so this pane's tooltip
                      also reports Drawdown (at dd buckets). Stays on the
                      hidden axis so negative dd values don't drag the money
                      scale of THIS pane down (the strip below plots it). */}
                  <Line
                    yAxisId="dd"
                    type="monotone"
                    dataKey="ddAbs"
                    stroke="none"
                    dot={false}
                    activeDot={false}
                    connectNulls
                    isAnimationActive={false}
                    legendType="none"
                  />
                  {epElements}
                  {equityExtremeDots}
                  {dragSelection}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            {/* ── drawdown strip ── slim, carries the shared x-axis labels.
                 Tooltip content is suppressed (values are in the pane above);
                 the Tooltip component stays for the synced cursor line. */}
            <div
              className="h-[96px] select-none sm:h-[110px] md:h-[124px]"
              onDoubleClick={resetZoom}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData}
                  syncId="eqdd"
                  onMouseDown={onDragDown}
                  onMouseMove={onDragMove}
                  onMouseUp={onDragUp}
                  onMouseLeave={cancelDrag}
                >
                  {ddGradient}
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis
                    type="number"
                    dataKey="ts"
                    domain={xDomain}
                    ticks={xTicks}
                    tickFormatter={fmtXTick}
                    tick={AXIS}
                    allowDataOverflow
                  />
                  {/* width matches the equity pane so the plots align;
                      currency scale so the strip reads in the same units
                      as the equity pane above */}
                  <YAxis
                    tick={AXIS}
                    width={70}
                    domain={stripDdDomain}
                    allowDataOverflow
                    tickFormatter={(v) => v.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  />
                  <Tooltip content={() => null} />
                  <Area
                    type="monotone"
                    dataKey="ddAbs"
                    stroke="#ef4444"
                    strokeWidth={1.5}
                    fill="url(#eqdd-dd-fill)"
                    connectNulls
                    isAnimationActive={false}
                  />
                  {ddTroughDot('depth')}
                  {dragSelection}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          /* ── overlay ── one plot, two y axes: money left, dd % right.
               The dd area hangs from the top edge (compressed domain) behind
               the balance/equity lines. */
          <div
            className="h-[300px] select-none sm:h-[360px] md:h-[400px]"
            onDoubleClick={resetZoom}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                onMouseDown={onDragDown}
                onMouseMove={onDragMove}
                onMouseUp={onDragUp}
                onMouseLeave={cancelDrag}
              >
                {ddGradient}
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis
                  type="number"
                  dataKey="ts"
                  domain={xDomain}
                  ticks={xTicks}
                  tickFormatter={fmtXTick}
                  tick={AXIS}
                  allowDataOverflow
                />
                {/* ONE money axis — dd is in currency, so it shares the
                    scale with balance/equity (no right-side % axis). */}
                <YAxis
                  tick={AXIS}
                  domain={moneyDomain}
                  width={70}
                  allowDataOverflow
                  tickFormatter={(v) => v.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                />
                <Tooltip
                  contentStyle={TooltipStyle}
                  labelStyle={{ color: '#9ca3af' }}
                  formatter={tooltipFormatter}
                  labelFormatter={(l) => fmtTooltipLabel(l as number)}
                />
                {markerLines}
                {/* dd first so the lines render on top of the band. Range
                    area [balance − |dd|, balance]: the drawdown hangs off
                    the balance line as the LEVEL the account fell to, so
                    its size reads directly against balance/equity. */}
                <Area
                  type="monotone"
                  dataKey="ddBand"
                  stroke="#ef4444"
                  strokeWidth={1}
                  fill="url(#eqdd-dd-fill)"
                  connectNulls
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="balance"
                  stroke="#00ff88"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="equity"
                  stroke="#60a5fa"
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
                {epElements}
                {equityExtremeDots}
                {ddTroughDot('level')}
                {dragSelection}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </PanelBody>
    </Panel>
  );
}
